import * as React from 'react';
import { isAlphaNumericKey, isNavigationKey } from './keyCodeCheckings';
import { getCellProperty } from './getCellProperty';
import { keyCodes } from './keyCodes';
import { getCharFromKey } from './getCharFromKeyCode';
import { TbClipboardText } from 'react-icons/tb';
import commonStore from '../../store/common';
import ReportCellComment from '../../pages/Report/components/ReportComments/ReportCellComment';
import * as Styled from './style';
import { Text, Tooltip } from '@mantine/core';
import { getCellState, isCellEmpty } from '../../utils/cellTemplate';

const jurisdictionFields = ['version', 'aifm_national_code', 'aif_national_code'];

// Lazy-mounts the comment icon/popover after the first hover (see SelectCellView
// in NewSelectCellTemplate for the rationale). Only used in display mode.
const TextCellView = ({ cell }: { cell: any }) => {
  const [commentMounted, setCommentMounted] = React.useState(false);
  const isEmptyValue = isCellEmpty(cell.text);
  const { isDisabled, background } = getCellState(cell, isEmptyValue);
  const isJurisdictionField = jurisdictionFields.includes(cell.dataKey);
  const view = (
    <Styled.ValueContainer
      style={{
        background
      }}
      data-key={`${cell.dataKey}-${cell.dataIndex}-${cell.tableName}-${cell.fundId}${cell.sub_asset_type ? `-${cell.sub_asset_type}` : ''}`}
      className="report-field"
      onPointerEnter={() => setCommentMounted(true)}
    >
      <div className="text">
        <p
          style={{
            color: isDisabled ? '#909296' : isEmptyValue && cell.required ? '#FF6B6B' : '#000',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}
        >
          {isEmptyValue && cell.required ? 'This field is mandatory' : cell.text}
          {cell?.difference?.is_changed && (
            <Text fz="12px" c="orange.6">
              Changed
            </Text>
          )}
        </p>

        {cell.text && cell.isStaticField && (
          <TbClipboardText style={{ minWidth: '12px' }} fontSize="12px" />
        )}
      </div>
      {!!cell.errorMessage && <p className="error-message">{cell.errorMessage}</p>}
      <ReportCellComment
        fundId={cell.fundId}
        fieldName={cell.dataKey}
        fundName={cell.fundName}
        tableName={cell.tableName}
        fieldOrder={cell.fieldOrder}
        cellHovered={commentMounted}
      />
    </Styled.ValueContainer>
  );

  if (!isJurisdictionField) return view;

  return (
    <Tooltip
      label="This field is populated from the Jurisdictions section and is editable exclusively from that page."
      maw="321px"
      multiline
      bg="gray.6"
      fz="12px"
      lh="18px"
      p="8px"
      zIndex={99999}
    >
      {view}
    </Tooltip>
  );
};

export class NewTextCellTemplate {
  wasEscKeyPressed = false;

  getCompatibleCell(uncertainCell: any) {
    const text = getCellProperty(uncertainCell, 'text', 'string');
    let placeholder;
    try {
      placeholder = getCellProperty(uncertainCell, 'placeholder', 'string');
    } catch {
      placeholder = '';
    }
    const value = parseFloat(text); // TODO more advanced parsing for all text based cells
    return { ...uncertainCell, text, value, placeholder };
  }

  update(cell: any, cellToMerge: any) {
    return this.getCompatibleCell({
      ...cell,
      text: cellToMerge.text,
      placeholder: cellToMerge.placeholder
    });
  }

  handleKeyDown(
    cell: any,
    keyCode: number,
    ctrl: boolean,
    shift: boolean,
    alt: boolean,
    key: string,
    capsLock: boolean
  ) {
    const char = getCharFromKey(key, shift, capsLock);

    if (!ctrl && !alt && isAlphaNumericKey(keyCode) && !(shift && keyCode === keyCodes.SPACE))
      return {
        cell: this.getCompatibleCell({ ...cell, text: char }),
        enableEditMode: true
      };
    return {
      cell,
      enableEditMode: keyCode === keyCodes.POINTER || keyCode === keyCodes.ENTER
    };
  }

  handleCompositionEnd(cell: any, eventData: string) {
    if (eventData) {
      return { cell: { ...cell, text: eventData }, enableEditMode: true };
    } else {
      return { cell: { ...cell }, enableEditMode: true };
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  getClassName(cell: any, _isInEditMode: boolean) {
    const isValid = cell.validator ? cell.validator(cell.text) : true;
    const className = cell.className ? cell.className : '';

    return `${isValid ? 'valid' : 'rg-invalid'} ${
      cell.placeholder && cell.text === '' ? 'placeholder' : ''
    } ${className}`;
  }

  // Extracted inline event handlers - preserving exact same logic
  handleInputChange =
    (cell: any, onCellChanged: (cell: any, commit: boolean) => void) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onCellChanged(this.getCompatibleCell({ ...cell, text: e.currentTarget.value }), false);
    };

  handleInputBlur =
    (cell: any, onCellChanged: (cell: any, commit: boolean) => void) =>
    (e: React.FocusEvent<HTMLInputElement>) => {
      onCellChanged(
        this.getCompatibleCell({ ...cell, text: e.currentTarget.value }),
        !this.wasEscKeyPressed
      );
      this.wasEscKeyPressed = false;
    };

  handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (isAlphaNumericKey(e.keyCode) || isNavigationKey(e.keyCode)) e.stopPropagation();
    if (e.keyCode === keyCodes.ESCAPE) this.wasEscKeyPressed = true;
  };

  render(cell: any, isInEditMode: boolean, onCellChanged: (cell: any, commit: boolean) => void) {
    if (!isInEditMode) {
      return <TextCellView cell={cell} />;
    }

    return (
      <Styled.StyledInput
        className="rg-input"
        ref={input => {
          if (input) {
            input.focus();
            input.setSelectionRange(input.value.length, input.value.length);
          }
        }}
        style={{
          border: 'none'
        }}
        disabled={(!cell.isStaticTemplate && cell.isStaticField) || commonStore.isViewer}
        defaultValue={cell.text}
        onChange={this.handleInputChange(cell, onCellChanged)}
        onBlur={this.handleInputBlur(cell, onCellChanged)}
        onCopy={e => e.stopPropagation()}
        onCut={e => e.stopPropagation()}
        onPaste={e => e.stopPropagation()}
        onPointerDown={e => e.stopPropagation()}
        placeholder={cell.placeholder}
        onKeyDown={this.handleInputKeyDown}
      />
    );
  }
}
